import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { supabaseAuth } from "./supabase-auth-config";
import { restoreReturnPath, redirectToAuth } from "@/lib/auth-redirect";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { PulseLogo } from "@pulse-business-ai/ui";

const CALLBACK_TIMEOUT_MS = 15_000;
const AUTO_REDIRECT_DELAY_MS = 2_000;

function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div className="pulse-brand-surface flex min-h-screen flex-col items-center justify-center gap-6 px-6 py-10">
      <PulseLogo size="md" href="https://pulsebusiness.ai" />
      {children}
    </div>
  );
}

export function CallbackPage() {
  const navigate = useNavigate();
  const processed = useRef(false);
  const [timedOut, setTimedOut] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [autoRedirecting, setAutoRedirecting] = useState(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // Don't touch state/navigation after unmount: on a slow run the auth
    // promise can settle after Vitest tears down the jsdom environment, and a
    // setState/navigate then reaches into react-dom internals expecting
    // `window` → "ReferenceError: window is not defined" → unhandled rejection
    // that fails the whole test run. Same class as pulse-business #637.
    let cancelled = false;
    let redirectTimer: ReturnType<typeof setTimeout> | undefined;

    const timeout = setTimeout(() => setTimedOut(true), CALLBACK_TIMEOUT_MS);

    async function handleCallback() {
      // Accept tokens from either the hash fragment (legacy Supabase OAuth flow)
      // or query params (shared auth portal handoff via buildRedirectUrlWithTokens).
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash || window.location.search);

      const errorCode = params.get("error");
      const errorDescription = params.get("error_description");
      if (errorCode) {
        throw new Error(errorDescription || errorCode);
      }

      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");

      if (!accessToken || !refreshToken) {
        const { data: { session }, error } = await supabaseAuth.auth.getSession();
        if (error) throw error;
        if (!session) {
          throw new Error("No tokens in callback URL and no active session");
        }
        return session;
      }

      const { data: { session }, error } = await supabaseAuth.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) throw error;
      if (!session) throw new Error("setSession returned null session");

      return session;
    }

    handleCallback()
      .then(() => {
        clearTimeout(timeout);
        if (cancelled) return;
        // H5: do not pass document.title here — keep the static index.html title.
        window.history.replaceState({}, "", window.location.pathname);
        // H4: portal-scoped restore (with legacy-key fallback for in-flight sessions).
        const returnTo =
          restoreReturnPath() ??
          (() => {
            const legacy = sessionStorage.getItem("agent_hub_return_to");
            sessionStorage.removeItem("agent_hub_return_to");
            return legacy;
          })() ??
          "/";
        navigate(returnTo, { replace: true });
      })
      .catch((err) => {
        clearTimeout(timeout);
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Authentication failed";
        console.error("[Auth] Callback error:", msg);
        setErrorMsg(msg);

        const lower = msg.toLowerCase();
        const isRecoverable = ["invalid", "expired", "already used", "no tokens", "pkce"].some(
          (p) => lower.includes(p),
        );

        if (isRecoverable) {
          setAutoRedirecting(true);
          // H8: bounce straight to the auth gateway (no `/auth` SPA hop).
          redirectTimer = setTimeout(() => {
            if (!cancelled) redirectToAuth("/");
          }, AUTO_REDIRECT_DELAY_MS);
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [navigate]);

  if (errorMsg) {
    return (
      <AuthFrame>
        <div className="max-w-sm rounded-xl border border-red-800/60 bg-red-950/40 px-5 py-3 text-center text-sm text-red-300">
          <div className="mb-1 flex items-center justify-center gap-2">
            <AlertCircle size={16} strokeWidth={2} aria-hidden="true" />
            <strong>Sign-in failed</strong>
          </div>
          {errorMsg}
        </div>
        {autoRedirecting ? (
          <p className="text-sm text-slate-400">Redirecting to login…</p>
        ) : (
          <button
            type="button"
            onClick={() => redirectToAuth("/")}
            className="rounded-lg px-5 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-lg active:scale-95"
            style={{ background: "var(--pulse-primary-gradient)" }}
          >
            Return to login
          </button>
        )}
      </AuthFrame>
    );
  }

  if (timedOut) {
    return (
      <AuthFrame>
        <p className="text-sm text-slate-400">Sign-in is taking longer than expected.</p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800/60 px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-700/80"
          >
            <RefreshCw size={14} strokeWidth={2} aria-hidden="true" />
            Retry
          </button>
          <button
            type="button"
            onClick={() => redirectToAuth("/")}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-lg"
            style={{ background: "var(--pulse-primary-gradient)" }}
          >
            Return to login
          </button>
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <div className="flex items-center gap-2 text-slate-400">
        <Loader2 size={18} strokeWidth={2} className="animate-spin" aria-hidden="true" />
        <span className="text-sm">Completing sign-in…</span>
      </div>
    </AuthFrame>
  );
}
